/**
 * StyleProperties — CSS-like style descriptor produced by the StyleBuilder component.
 *
 * Mirrors the interface in @atlas-forms/designer-components-react/StyleBuilderPanel/types.ts.
 * Once StyleProperties is published to @atlas-forms/types-js, replace this file with a
 * re-export from that package.
 */

export interface StyleProperties {
  backgroundColor?: string;
  color?: string;
  borderColor?: string;
  textShadow?: string;
  backgroundImage?: string;
  backgroundSize?: string;
  backgroundPosition?: string;
  backgroundRepeat?: 'repeat' | 'no-repeat' | 'repeat-x' | 'repeat-y';
  backdropFilter?: string;
  filter?: string;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold' | number;
  fontStyle?: 'normal' | 'italic';
  lineHeight?: number;
  letterSpacing?: number;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  textDecoration?: 'none' | 'underline' | 'line-through' | 'overline';
  textOverflow?: 'clip' | 'ellipsis';
  whiteSpace?: 'normal' | 'nowrap' | 'pre' | 'pre-wrap';
  wordBreak?: 'normal' | 'break-word' | 'break-all';
  paddingTop?: number;
  paddingRight?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
  gap?: number;
  rowGap?: number;
  columnGap?: number;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  height?: string;
  minHeight?: string;
  maxHeight?: string;
  aspectRatio?: string;
  objectFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down';
  objectPosition?: string;
  overflow?: 'visible' | 'hidden' | 'scroll' | 'auto';
  overflowX?: 'visible' | 'hidden' | 'scroll' | 'auto';
  overflowY?: 'visible' | 'hidden' | 'scroll' | 'auto';
  borderWidth?: number;
  borderTopWidth?: number;
  borderRightWidth?: number;
  borderBottomWidth?: number;
  borderLeftWidth?: number;
  borderStyle?: 'none' | 'solid' | 'dashed' | 'dotted' | 'double';
  borderTopStyle?: 'none' | 'solid' | 'dashed' | 'dotted';
  borderRightStyle?: 'none' | 'solid' | 'dashed' | 'dotted';
  borderBottomStyle?: 'none' | 'solid' | 'dashed' | 'dotted';
  borderLeftStyle?: 'none' | 'solid' | 'dashed' | 'dotted';
  borderTopColor?: string;
  borderRightColor?: string;
  borderBottomColor?: string;
  borderLeftColor?: string;
  borderRadius?: number;
  borderTopLeftRadius?: number;
  borderTopRightRadius?: number;
  borderBottomRightRadius?: number;
  borderBottomLeftRadius?: number;
  outlineWidth?: number;
  outlineStyle?: 'none' | 'solid' | 'dashed' | 'dotted';
  outlineColor?: string;
  outlineOffset?: number;
  boxShadow?: string;
  opacity?: number;
  transform?: string;
  transition?: string;
  cursor?: string;
  userSelect?: 'none' | 'text' | 'all' | 'auto';
  pointerEvents?: 'none' | 'auto';
  display?: 'block' | 'flex' | 'grid' | 'inline-block' | 'inline-flex';
  flexDirection?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  justifyContent?:
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'space-evenly';
  alignItems?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  flexWrap?: 'nowrap' | 'wrap' | 'wrap-reverse';
  clipPath?: string;
  position?: 'static' | 'relative' | 'absolute' | 'sticky' | 'fixed';
  zIndex?: number;
}

const PX_FIELDS = new Set<keyof StyleProperties>([
  'fontSize', 'letterSpacing',
  'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
  'gap', 'rowGap', 'columnGap',
  'borderWidth', 'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
  'borderRadius', 'borderTopLeftRadius', 'borderTopRightRadius',
  'borderBottomRightRadius', 'borderBottomLeftRadius',
  'outlineWidth', 'outlineOffset',
])

/**
 * Convert a StyleProperties object to React.CSSProperties.
 * Numbers are treated as px for dimensional properties; unitless for lineHeight, opacity, zIndex.
 */
export function stylesToCSS(s: StyleProperties): React.CSSProperties {
  const result: Record<string, unknown> = {}

  for (const [key, val] of Object.entries(s)) {
    if (val === undefined || val === null) continue
    const k = key as keyof StyleProperties
    if (typeof val === 'number' && PX_FIELDS.has(k)) {
      result[key] = `${val}px`
    } else {
      result[key] = val
    }
  }

  return result as React.CSSProperties
}
